import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Table, Pagination, Icon, Button, SpinnerCircular, Notification } from "@weirgroup/weir-dls"

import { getTableHeaders, groupData } from "../../utils/table.util";
import { notificationTableHeaders } from "../../constants/notification.constants";
import { SettingsModal } from "./components/settings-modal";
import "./styles.scss";
import dayjs from "dayjs";
import { clearNotifications, deleteSingleNotification, getUserNotifications } from "../../api/notifications.api";
import { NotifTableDataType, NotificationType } from "../../types/notification.type";
import { ConfirmDialog } from "../../components/confirm-dialog/confirm-dialog";
import settingsApi from "../../api/settings.api";
import { FetchedUserSettingType, RolesType } from "../../types/user.type";
import { StorageService, ROLES } from "../../services/storage_service";
import { Roles } from "../../constants/roles.constants";
import { useDateConverter } from "../../hooks/use-date-converter";
import { useDarkMode } from "../../hooks/use-darkmode";
import localizedFormat from 'dayjs/plugin/localizedFormat';

// Extend dayjs with the localizedFormat plugin
dayjs.extend(localizedFormat);
export const NotificationPage = () => {
    const { t } = useTranslation();
    const [headers, setHeaders] = useState<any>();
    const [source, setSource] = useState<any>();
    const [isSettingsModalShown, setIsSettingsModalShown] = useState<boolean>(false);
    const sevenDaysAgo = dayjs().subtract(7, 'days');
    const [startDate, setStartDate] = useState<string>(dayjs(sevenDaysAgo).format('l'));
    const [endDate, setEndDate] = useState<string>(dayjs().format('l'));
    const [isConfirmOpen, setIsConfirmOpen] = useState<boolean>(false);
    const [notificationData, setNotificationData] = useState<NotifTableDataType[]>([])
    const [toastData, setToastData] = useState<any>()
    const [showToast, setShowToast] = useState<boolean>(false)
    const [userSettings, setUserSettings] = useState<any>()
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [confirmData, setConfirmData] = useState<any>();
    const rowCount = 10;
    const [currentPage, setCurrentPage] = useState<number>(1);
    const {convertDateStringToFormat} = useDateConverter();
    const {darkMode} = useDarkMode();
    const tableDeleteIcon = (notification_id: number) => {
        return (
            <Icon
                darkMode={darkMode}
                name={"DXPClose"}
                onClick={() => {
                    if (!isGuest) {
                        setConfirmData({
                            handler: () => deleteNotification(notification_id),
                            message: t('deleteSingleNotifMessage')
                        })
                        setIsConfirmOpen(true)
                    } else {
                        alert(t('GuestNotificationMessage'))
                    }

                }}
                disabled={isGuest}
            />
        )

    }

    const [isGuest, setIsGuest] = useState<boolean>(false);
    const roleData = StorageService.getItem(ROLES);

    useEffect(() => {
        if (roleData) {
            const roles: RolesType = JSON.parse(roleData)
            setIsGuest(roles.role == Roles.GUEST)
        }
    }, [roleData])

    useEffect(() => {
        let groupedData = groupData(notificationData, rowCount)
        setSource(groupedData);
    }, [notificationData])

    const fetchNotificationConfigs = async () => {
        const fetchedData = await settingsApi.getUserSettings();
        const settings: FetchedUserSettingType = fetchedData.data.data;
        setUserSettings(settings);
    }

    const fetchNotifications = () => {
        const dateRange = { startDate, endDate }
        getUserNotifications(dateRange).then((result: any) => {
            let notifdata = result.data.data;
            let newData: NotifTableDataType[] = notifdata.map((data: NotificationType) => {
                let convertedDate = convertDateStringToFormat(data.updated_at, { hideTime: true })
                return {
                    serial_number: data.asset.bucket.serial_number,
                    tag_id: data.asset.tag.tag_id,
                    date: convertedDate,
                    location: data.payload.location_name ?? "--",
                    message: data.subject,
                    action: tableDeleteIcon(data.notification_id)
                }
            })
            setNotificationData(newData);
            setIsLoading(false)
        }).catch((err: any) => {
            setIsLoading(false)
            console.error(err);
        })
    }

    const processNotification = (isSuccessful: boolean) => {
        setToastData({
            variant: isSuccessful ? 'success' : 'critical',
            message: isSuccessful ? t('notificationDeletedSuccess') : t('notificationDeletedFail')
        })
        setShowToast(true)
        setIsLoading(false)
        setIsConfirmOpen(false)
        fetchNotifications();
    }

    const deleteNotification = (notification_id: number) => {
        setIsLoading(true)
        deleteSingleNotification(notification_id).then(() => {
            processNotification(true)
        }).catch(() => {
            processNotification(false)
        })
    }

    const clearNotificationList = () => {
        clearNotifications().then(() => {
            processNotification(true)
        }).catch(() => {
            processNotification(false)
        })
    }

    useEffect(() => {
        setIsLoading(true)
        const tableHeaders = getTableHeaders(notificationTableHeaders)
        setHeaders(tableHeaders);
        fetchNotificationConfigs()
        fetchNotifications();
    }, [])

    useEffect(() => {
        if (showToast) {
            setTimeout(() => setShowToast(false), 5000)
        }
    }, [toastData])

    const displayNoResults = () => {
        return !isLoading ?
            <div className="no-result">
                <p style={{color:darkMode ? "white": "black"}}>{t("noNotifications")}</p>
                <div className="no-result-btn">
                    <Button
                        darkMode={darkMode}
                        label={t("setDateRange")}
                        size={"sm"}
                        onClick={() => setIsSettingsModalShown(true)}
                    />
                </div>
            </div> :
            <></>
    }

    return (
        <div className="notification-page-container" style={{backgroundColor:darkMode ? 'rgb(59, 67, 76)' : 'white'}}>
            {headers && source && !isLoading ?
                <>
                    {isGuest ?
                        <Table
                            darkMode={darkMode}
                            sortable={true}
                            header={headers}
                            source={source[currentPage - 1]}
                            title={t("notificationTableTitle")}
                            noResultsText={t("noResults")}
                            tableType={"default"}
                            canvasDeviceSize={'none'}
                        />
                        :
                        <Table
                            darkMode={darkMode}
                            sortable={true}
                            header={headers}
                            source={source[currentPage - 1]}
                            title={t("notificationTableTitle")}
                            noResultsText={t("noResults")}
                            tableType={"default"}
                            canvasDeviceSize={'none'}
                            primaryActionBtnTitle={t("clear")}
                            onPrimaryActionBtnClick={() => {
                                setConfirmData({
                                    handler: () => clearNotificationList(),
                                    message: t('clearNotificationMessage')
                                })
                                setIsConfirmOpen(true)
                            }}
                            secondaryActionBtnTitle={t("settings")}
                            onSecondaryActionBtnClick={() => setIsSettingsModalShown(true)}
                        />
                    }
                    <div className="pagination">
                        <Pagination
                            darkMode={darkMode}
                            currentPage={currentPage}
                            totalRows={source.length}
                            pageRows={0}
                            onPrevious={() => setCurrentPage(currentPage > 1 ? currentPage - 1 : 1)}
                            onNext={() => setCurrentPage(currentPage < source?.length ? currentPage + 1 : source?.length)}
                            onSelect={(index: number) => setCurrentPage(index)} />
                    </div>
                </>
                :
                displayNoResults()

            }
            {isSettingsModalShown ?
                <SettingsModal
                    darkMode={darkMode}
                    isSettingsModalShown={isSettingsModalShown}
                    setIsSettingsModalShown={setIsSettingsModalShown}
                    startDate={startDate}
                    setStartDate={setStartDate}
                    endDate={endDate}
                    setEndDate={setEndDate}
                    fetchNotifications={fetchNotifications}
                    setToastData={setToastData}
                    setShowToast={setShowToast}
                    userSettings={userSettings}
                    fetchNotificationConfigs={fetchNotificationConfigs}
                /> : <></>}
            {isConfirmOpen ?
                <ConfirmDialog
                    isConfirmOpen={isConfirmOpen}
                    cancelHandler={() => setIsConfirmOpen(false)}
                    confirmHandler={confirmData.handler}
                    confirmMessage={confirmData.message}
                />
                : <></>
            }
            {showToast ? <div className="toast-container">
                <Notification 
                    darkMode={darkMode}
                    title={toastData.variant == "success" ? "Success" : "Fail"}
                    textContent={toastData.message}
                    variant={toastData.variant}
                    onClose={()=>{setShowToast(false)}}
                />
            </div> : <></>}
            {isLoading ?
                <div className="spinner">
                    <SpinnerCircular darkMode={darkMode} />
                </div> : <></>
            }
        </div>
    )
}